import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { PopupSize, PopupTemplate, ButtonColorTheme, ParticipantPopupOperationType } from "..";
import { RequestResult, timeConvert, TimeConvertType } from "../../lib";
import {
  useCreateDispatchActions,
  useSelectParticipantAdherence,
  useSelectTheme,
} from "../../store";
import { IParticipant } from "../../shared/db";

export const PopupCompleteDataCollection = (props: {
  setOperationType: Dispatch<SetStateAction<ParticipantPopupOperationType | null>>;
  participant: IParticipant | null;
}) => {
  const { setOperationType, participant } = props;
  const { color, fontSize, fontWeight } = useSelectTheme();
  const { doREQUEST_PARTICIPANT_COMPLETE_STATUS_UPDATE, doBATCH_UPDATE_PARTICIPANT } =
    useCreateDispatchActions();
  const [requestResult, setRequestResult] = useState<RequestResult | null>(null);
  const isParticipantMarkCompleted =
    participant?.completedDate != null || participant?.markCompletedAt != null;
  const participantProjectId = participant?.projectId ?? "";
  const participantId = participant?.id ?? "";
  const participantAdherence = useSelectParticipantAdherence(participantProjectId, participantId);
  const lastSyncCompleteTimestamp = participantAdherence?.status?.unixTimestampLastGarminDeviceSync;
  const lastDataReceived = lastSyncCompleteTimestamp
    ? (timeConvert({ time: lastSyncCompleteTimestamp, type: TimeConvertType.lastSeen }) as string)
    : "";

  const handleClosePopup = () => {
    setOperationType(null);
  };

  const handleConfirm = () => {
    if (!participant?.id) return;
    if (isParticipantMarkCompleted) {
      doREQUEST_PARTICIPANT_COMPLETE_STATUS_UPDATE({
        setRequestResult,
        payload: {
          participantId: participant.id,
          complete: false,
        },
      });
      return;
    }

    doREQUEST_PARTICIPANT_COMPLETE_STATUS_UPDATE({
      setRequestResult,
      payload: {
        participantId: participant.id,
        complete: true,
        force: true,
      },
    });
  };

  useEffect(() => {
    if (!requestResult?.success) return;
    if (participant?.batchId) {
      const currentDate = new Date();
      doBATCH_UPDATE_PARTICIPANT({
        batchId: participant.batchId,
        participant: {
          ...participant,
          completedDate: isParticipantMarkCompleted ? null : currentDate,
          markCompletedAt: isParticipantMarkCompleted ? null : currentDate,
        },
      });
    }
    handleClosePopup();
  }, [requestResult]);

  return (
    <PopupTemplate
      {...{
        title: isParticipantMarkCompleted ? "Resume Data Collection" : "Complete Data Collection",
        description: isParticipantMarkCompleted ? (
          <p
            css={{
              margin: 0,
              color: color.textIcon.secondary,
            }}
          >
            Reactivate this participant to generate an invite code so they can log in again. Their
            status will show as 'Joined' if they have participated in the past. Note: Dates are
            based on when the participant first joined the project. i.e., if they first joined 50
            days ago, it will be Day 51 when they rejoin.
          </p>
        ) : (
          <div
            css={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <p
              css={{
                margin: 0,
                color: color.textIcon.secondary,
              }}
            >
              Doing this will end data collection, meaning participants will log out after they have
              completed data upload and stop collecting data from any API streams (ie: Garmin
              Connect) if you have it enabled.
            </p>
            {lastDataReceived ? (
              <div
                css={{
                  fontSize: fontSize.h6,
                  color: color.textIcon.secondary,
                }}
              >
                <span>Last data received: </span>
                <span
                  css={{
                    color: color.textIcon.link,
                    fontWeight: fontWeight.thick,
                  }}
                >
                  {lastDataReceived}
                </span>
              </div>
            ) : null}
          </div>
        ),
        background: true,
        popupSize: PopupSize.Medium,
        handleClosePopup: handleClosePopup,
        mainFunction: handleConfirm,
        buttonContent: isParticipantMarkCompleted
          ? "Resume data collection"
          : "Complete Data Collection",
        buttonColorTheme: isParticipantMarkCompleted
          ? ButtonColorTheme.Primary
          : ButtonColorTheme.Warning,
      }}
    />
  );
};
